import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Invoice, InvoiceStatus } from 'shared';

interface InvoiceDataTableProps {
  invoices: Invoice[];
  loading: boolean;
  selectedInvoices: Invoice[];
  onSelectionChange: (invoices: Invoice[]) => void;
  onPreview: (invoice: Invoice) => void;
  onDownload: (invoice: Invoice) => void;
  onApprove: (invoice: Invoice) => void;
  onSend: (invoice: Invoice) => void;
  onResend: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onMarkAsPaid?: (invoice: Invoice) => void;
}

export const InvoiceDataTable: React.FC<InvoiceDataTableProps> = ({
  invoices,
  loading,
  selectedInvoices,
  onSelectionChange,
  onPreview,
  onDownload,
  onApprove,
  onSend,
  onResend,
  onDelete,
  onMarkAsPaid,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const invoiceNumberBodyTemplate = (invoice: Invoice) => {
    const isCustom = invoice.invoiceNumber.includes('-C');
    return (
      <div className="flex align-items-center gap-2">
        <span>{invoice.invoiceNumber}</span>
        {isCustom && (
          <Tag value="CUSTOM" severity="info" className="text-xs" />
        )}
      </div>
    );
  };

  const statusBodyTemplate = (invoice: Invoice) => {
    const severityMap: Record<string, "warning" | "info" | "success" | "danger" | "secondary" | null> = {
      [InvoiceStatus.DRAFT]: 'warning',
      [InvoiceStatus.APPROVED]: 'info',
      [InvoiceStatus.SENT]: 'success',
      [InvoiceStatus.CANCELLED]: 'danger',
      [InvoiceStatus.PAID]: null,
      [InvoiceStatus.OVERDUE]: 'warning',
    };

    const isPaid = invoice.status === InvoiceStatus.PAID;
    const isOverdue = invoice.status === InvoiceStatus.OVERDUE;

    return (
      <Tag
        value={invoice.status}
        severity={severityMap[invoice.status] ?? undefined}
        style={isPaid ? { backgroundColor: '#14b8a6', color: 'white' } : isOverdue ? { backgroundColor: '#f97316', color: 'white' } : undefined}
      />
    );
  };

  const purposeBodyTemplate = (invoice: Invoice) => {
    return invoice.purpose ? (
      <span className="text-sm">{invoice.purpose}</span>
    ) : (
      <span className="text-400">-</span>
    );
  };

  const memberBodyTemplate = (invoice: Invoice) => {
    return (
      <div>
        <div className="font-semibold">{`${invoice.member?.firstName} ${invoice.member?.lastName}`}</div>
        <div className="text-sm text-500">{invoice.member?.memberNumber}</div>
      </div>
    );
  };

  const amountBodyTemplate = (invoice: Invoice) => {
    return <span className="font-semibold">{formatCurrency(parseFloat(invoice.amount.toString()))}</span>;
  };

  const dateBodyTemplate = (invoice: Invoice) => {
    return formatDate(invoice.invoiceDate);
  };

  const deadlineBodyTemplate = (invoice: Invoice) => {
    return formatDate(invoice.paymentDeadline);
  };

  const actionsBodyTemplate = (invoice: Invoice) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => onPreview(invoice)}
          tooltip="Preview"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => onDownload(invoice)}
          tooltip="Download"
          tooltipOptions={{ position: 'top' }}
        />

        {invoice.status === InvoiceStatus.DRAFT && (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-rounded p-button-text p-button-success p-button-sm"
              onClick={() => onApprove(invoice)}
              tooltip="Approve"
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-text p-button-danger p-button-sm"
              onClick={() => onDelete(invoice)}
              tooltip="Delete"
              tooltipOptions={{ position: 'top' }}
            />
          </>
        )}

        {invoice.status === InvoiceStatus.APPROVED && (
          <Button
            icon="pi pi-send"
            className="p-button-rounded p-button-text p-button-info p-button-sm"
            onClick={() => onSend(invoice)}
            tooltip="Send"
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {(invoice.status === InvoiceStatus.SENT || invoice.status === InvoiceStatus.OVERDUE) && (
          <>
            {onMarkAsPaid && (
              <Button
                icon="pi pi-check-circle"
                className="p-button-rounded p-button-text p-button-sm"
                style={{ color: '#14b8a6' }}
                onClick={() => onMarkAsPaid(invoice)}
                tooltip="Mark Paid"
                tooltipOptions={{ position: 'top' }}
              />
            )}
            <Button
              icon="pi pi-refresh"
              className="p-button-rounded p-button-text p-button-sm"
              onClick={() => onResend(invoice)}
              tooltip="Resend"
              tooltipOptions={{ position: 'top' }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <DataTable
      value={invoices}
      loading={loading}
      selection={selectedInvoices}
      onSelectionChange={(e) => onSelectionChange(e.value)}
      dataKey="id"
      paginator
      rows={20}
      rowsPerPageOptions={[10, 20, 50, 100]}
      emptyMessage="No invoices found"
      className="mt-3 p-datatable-sm"
      scrollable
      scrollHeight="600px"
    >
      <Column selectionMode="multiple" headerStyle={{ width: '2.5rem' }} />
      <Column header="Actions" body={actionsBodyTemplate} style={{ width: 'auto', minWidth: '80px' }} />
      <Column header="Member" body={memberBodyTemplate} sortable field="member.lastName" />
      <Column header="Amount" body={amountBodyTemplate} sortable field="amount" />
      <Column header="Status" body={statusBodyTemplate} sortable field="status" />
      <Column header="Invoice #" body={invoiceNumberBodyTemplate} sortable field="invoiceNumber" />
      <Column header="Purpose" body={purposeBodyTemplate} style={{ maxWidth: '200px' }} />
      <Column header="Date" body={dateBodyTemplate} sortable field="invoiceDate" />
      <Column header="Deadline" body={deadlineBodyTemplate} sortable field="paymentDeadline" />
    </DataTable>
  );
};
